import { Handle, Position } from 'reactflow';
import { Bot } from 'lucide-react';
import { Badge } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { VoiceAgentTestData } from '@simcheck/shared-types';

interface VoiceAgentTestNodeData {
  label?: string;
  voiceAgentTest?: Partial<VoiceAgentTestData>;
}

export default function VoiceAgentTestNode({
  data,
  selected,
}: {
  data: VoiceAgentTestNodeData;
  selected?: boolean;
}) {
  const va = data.voiceAgentTest;
  const cfg = va?.agentConfig;
  // Speech is opt-in; default (undefined/false) = DTMF-only.
  const dtmfOnly = !cfg?.allowSpeak;
  const goalSnippet = (cfg?.goal || '').trim();

  return (
    <div className={cn(
      "min-w-[190px] max-w-[240px] rounded-lg",
      "bg-card",
      "border-2 transition-all duration-fast",
      selected
        ? "border-category-voice shadow-glow-focus"
        : "border-category-voice/50 dark:border-category-voice/30 shadow-md hover:shadow-lg"
    )}>
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !border-2 !bg-category-voice !border-category-voice"
        style={{ top: '-6px' }}
      />

      {/* Node Header */}
      <div className="flex items-center gap-2 p-3 border-b border-border dark:border-border-subtle">
        <div className="w-6 h-6 rounded bg-category-voice/10 dark:bg-category-voice/20 flex items-center justify-center">
          <Bot className="w-3.5 h-3.5 text-category-voice" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground">
            AI Agent Call
          </div>
          <div className="text-xs text-muted-foreground/70 font-mono truncate">
            {va?.phoneNumber || 'Not configured'}
          </div>
        </div>
        <Badge variant="outline" className="border-category-voice text-category-voice">
          {dtmfOnly ? 'DTMF' : 'Voice'}
        </Badge>
      </div>

      {/* Node Body */}
      <div className="p-3 space-y-2">
        {goalSnippet && (
          <div className="text-xs">
            <span className="text-muted-foreground font-medium">Goal:</span>
            <span className="ml-1 text-foreground line-clamp-2">{goalSnippet}</span>
          </div>
        )}

        {va?.storeAs && (
          <div className="text-xs">
            <span className="text-category-voice font-medium font-mono">
              → ${va.storeAs}
            </span>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-between items-center pt-2 border-t border-border dark:border-border-subtle">
          <span className="text-xs text-muted-foreground/70">
            {va?.maxDurationSec ? `${va.maxDurationSec}s max` : ''}
          </span>
          {typeof cfg?.maxTurns === 'number' && (
            <span className="text-xs text-muted-foreground/70">
              {cfg.maxTurns} turns
            </span>
          )}
        </div>
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !border-2 !bg-category-voice !border-category-voice"
        style={{ bottom: '-6px' }}
      />
    </div>
  );
}
